import { Fragment } from 'react';
import { NOT_IN_EVAL, type AttrRow } from './property-attributes';
import style from './comparison-properties.module.scss';

/**
 * A multi-value cell (Property Volume, Lease Operating Expense Per Well) for one
 * side: each sub-value is a two-column line — a small tag on the left (e.g. "MBOE",
 * "Yr 1") and the value on the right — so the numbers line up across lines. On the
 * Target side (`b`) a per-line change appends its absolute + % delta, colored
 * green/red by direction.
 * @param {object} props - the attribute row and which side to render
 * @return {JSX.Element | null} The stacked breakdown values, or null
 */
export const BreakdownCell = ({
  row,
  side,
}: {
  row: AttrRow;
  side: 'a' | 'b';
}) => {
  const { breakdown } = row;
  if (!breakdown) return null;
  const valueOf = (line: NonNullable<AttrRow['breakdown']>[number]): string => {
    return side === 'a' ? line.aText : line.bText;
  };
  // Whole side absent from the package → one plain "Not in package eval", not a
  // tagged line per metric (mirrors InterestCell).
  if (breakdown.every((line) => valueOf(line) === NOT_IN_EVAL)) {
    return <span>{NOT_IN_EVAL}</span>;
  }
  // Two-column grid: the tag and value of each line are direct grid children (via
  // Fragment) so the value column aligns across every line regardless of tag width.
  return (
    <div className={style.interestCell}>
      {breakdown.map((line) => (
        <Fragment key={line.tag}>
          <span className={style.interestTag}>{line.tag}</span>
          <span className={style.interestValue}>
            {valueOf(line)}
            {side === 'b' && line.delta && (
              <span
                className={
                  line.delta.positive
                    ? style.deltaPositive
                    : style.deltaNegative
                }
              >
                {' '}
                [{line.delta.text}]
              </span>
            )}
          </span>
        </Fragment>
      ))}
    </div>
  );
};
